import { ProjectSidebarLayout } from './project-sidebar-layout';
import { ProjectDetails } from './show-project-details/show-project-details-component';
import type { ShowInternalProjectLayoutProps } from '@/types';
import {
  GraduationCap, FolderOpen, CheckCircle2, Users,
  MessageCircle, StickyNote, FileText, Brain
} from 'lucide-react';
import { InternalHeader } from './show-project-details/internal-header';
import { useScroll } from '@/hooks';
import { KanbanMilestones } from './kanban-milestones/kanban-milestones';
import ProjectResources from './project-resources';
import { ProjectUsers } from './project-users';
import { ProjectForums } from './project-forums';
import { ProjectNotes } from './project-notes/project-notes';
import { ProjectDocs } from './project-docs/project-docs';
import { toast } from 'sonner';
import Skills from './skills';
import { ProjectInstitutes } from './project-institutes';
import { useGetExternalProjectProgressQuery } from '@/state/project/project-api';
import { useTenant } from '@/hooks';
import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';


export const ShowInternalProjectLayout = ({
  projectDetails: rootProjectDetails,
  detailedId,
  passedProjectId,
  routeProjectId,
  isAssignedView,
  isCompany,
}: ShowInternalProjectLayoutProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isExternal = rootProjectDetails?.project_type === 2;
  const isReadOnly = isExternal && isCompany;

  // Root Template ID (e.g. 14 or 21)
  const templateId = passedProjectId || routeProjectId;

  // Assignment context in local state (e.g. 25)
  const [currentDetailedId, setCurrentDetailedId] = React.useState<number | undefined>(
    isReadOnly ? undefined : detailedId
  );

  useScroll({ from: 0, to: 460 });

  const isNotAssigned =
    rootProjectDetails?.project_status?.toString().toUpperCase() === 'NOT ASSIGNED' ||
    rootProjectDetails?.status === 1;

  useEffect(() => {
    if (!isAssignedView && rootProjectDetails?.project_mentor) {
      toast.info('Project assigned. Redirecting to your assigned list...', {
        description: 'You can now find this project in the Assigned tab.',
        duration: 3000,
      });
      const timer = setTimeout(() => navigate(-1), 800);
      return () => clearTimeout(timer);
    }
  }, [isAssignedView, rootProjectDetails?.project_mentor, navigate]);

  const { tenant } = useTenant();

  // Fetch project progress (Company + External only)
  const { data: progressData } = useGetExternalProjectProgressQuery(
    { tenant: tenant!, projectId: templateId! },
    { skip: !isReadOnly || !templateId || !tenant }
  );

  // Resolve assignment record (e.g. ID 25)
  const assignmentDetails = useMemo(() => {
    if (!isReadOnly || !progressData?.project_details?.length) return null;
    if (currentDetailedId) {
      const found = progressData.project_details.find((a: any) => a.id === currentDetailedId);
      if (found) return found;
    }
    return progressData.project_details[0];
  }, [isReadOnly, progressData, currentDetailedId]);

  // Merge assignment with root — but keep the template ID intact
  const effectiveProjectDetails = useMemo(() => {
    if (!assignmentDetails) return rootProjectDetails;
    return {
      ...rootProjectDetails,
      ...assignmentDetails,
      id: rootProjectDetails.id,
      project_id: rootProjectDetails.id,
      expected_skills: assignmentDetails.required_skills || rootProjectDetails.expected_skills || [],
    };
  }, [assignmentDetails, rootProjectDetails]);

  // Sync resolved assignment ID back into state
  useEffect(() => {
    if (isReadOnly && assignmentDetails) {
      const resolvedId = assignmentDetails.id;
      if (currentDetailedId !== resolvedId) setCurrentDetailedId(resolvedId);
    }
  }, [isReadOnly, assignmentDetails, currentDetailedId]);

  // ── All possible tabs ──────────────────────────────────────────────────────
  const tabs = [
    {
      value: 'project-overview',
      title: 'Project Overview',
      icon: <GraduationCap size={17} />,
      content: <ProjectDetails project={effectiveProjectDetails} isExternal={isExternal} />,
    },
    {
      value: 'project-resources',
      title: 'Project Resources',
      icon: <FolderOpen size={17} />,
      content: <ProjectResources project={rootProjectDetails} isReadOnly={isReadOnly} />,
    },
    {
      value: 'task-progress',
      title: 'Task Progress',
      icon: <CheckCircle2 size={17} />,
      content: (
        <KanbanMilestones
          projectId={isReadOnly ? (currentDetailedId || templateId) : currentDetailedId}
          projectDetails={effectiveProjectDetails}
          projectPk={templateId}
          isReadOnly={isReadOnly}
        />
      ),
    },
    {
      value: 'institutes',
      title: 'Institutes',
      icon: <GraduationCap size={17} />,
      content: (
        <ProjectInstitutes
          assignments={progressData?.project_details || []}
          project={rootProjectDetails}
          activeAssignmentId={currentDetailedId}
          onSelect={(id) => setCurrentDetailedId(id)}
        />
      ),
    },
    {
      value: 'skills',
      title: 'Skills',
      icon: <Brain size={17} />,
      content: <Skills projectDetails={effectiveProjectDetails} isReadOnly={isReadOnly} />,
    },
    {
      value: 'forum',
      title: 'Forum',
      icon: <MessageCircle size={17} />,
      content: (
        <ProjectForums
          projectId={templateId!}
          isExternal={isExternal}
          projectRefId={rootProjectDetails?.project_ref?.id}
          isReadOnly={isReadOnly}
        />
      ),
    },
    {
      value: 'users',
      title: 'Users',
      icon: <Users size={17} />,
      content: (
        <ProjectUsers
          projectId={templateId}
          resourceId={currentDetailedId}
          projectDetails={effectiveProjectDetails}
          isReadOnly={isReadOnly}
        />
      ),
    },
    {
      value: 'notes',
      title: 'Notes',
      icon: <StickyNote size={17} />,
      content: (
        <ProjectNotes
          projectId={templateId!}
          isExternal={isExternal}
          resourceId={currentDetailedId}
          isReadOnly={isReadOnly}
        />
      ),
    },
    {
      value: 'documents',
      title: 'Documents',
      icon: <FileText size={17} />,
      content: (
        <ProjectDocs
          projectId={templateId!}
          initialDocs={effectiveProjectDetails?.docs_project}
          isExternal={isExternal}
          resourceId={currentDetailedId}
          isReadOnly={isReadOnly}
        />
      ),
    },
  ];

  // ── Tab visibility rules ───────────────────────────────────────────────────
  const filteredTabs = tabs.filter((tab) => {
    // Institutes: ONLY for Company preview of External projects
    if (tab.value === 'institutes' && !isReadOnly) return false;

    // Not-assigned projects: only show overview + resources
    if (isNotAssigned) {
      return tab.value === 'project-overview' || tab.value === 'project-resources';
    }

    const isAssignedToSchool = Array.isArray(rootProjectDetails?.assigned_schools)
      ? rootProjectDetails.assigned_schools.length > 0
      : !!rootProjectDetails?.assigned_schools;

    const isCompanyPreview =
      isReadOnly && rootProjectDetails?.published && isAssignedToSchool;

    if (isCompanyPreview) {
      if (tab.value === 'notes') return false;
      // Hide users/docs until an institute is selected
      if (['users', 'documents'].includes(tab.value) && !currentDetailedId) return false;
      return [
        'project-overview', 'project-resources', 'institutes',
        'skills', 'documents', 'task-progress', 'users',
      ].includes(tab.value);
    }

    // Student / Mentor side: gate activity tabs behind project status
    const status = Number(rootProjectDetails?.project_status);
    const isStarted = status === 4 || status === 5 || (isExternal && status === 2);
    const restrictedTabs = ['forum', 'task-progress', 'notes', 'documents', 'users'];
    if (restrictedTabs.includes(tab.value) && !isStarted) return false;

    return true;
  });

  // Propagate isReadOnly into every tab's content clone (no-op if already set)
  const finalTabs = filteredTabs.map((tab) => {
    if (isReadOnly && tab.value !== 'project-overview' && tab.value !== 'project-resources') {
      return {
        ...tab,
        content: React.cloneElement(tab.content as React.ReactElement<any>, { isReadOnly: true }),
      };
    }
    return tab;
  });

  const requestedTab = searchParams.get('tab');
  const defaultTab = finalTabs.some((tab) => tab.value === requestedTab)
    ? requestedTab!
    : 'project-overview';

  // Validate against all project tabs rather than the temporarily filtered
  // list. Some tabs become available only after project/assignment data loads.
  // Clearing those values here would make a valid deep link fall back to the
  // overview tab permanently during a reload.
  useEffect(() => {
    if (requestedTab && !tabs.some((tab) => tab.value === requestedTab)) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('tab');
      setSearchParams(nextParams, { replace: true });
    }
  }, [requestedTab, searchParams, setSearchParams, tabs]);

  const handleTabChange = (tab: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <>
      <InternalHeader project={rootProjectDetails} internalProjectId={templateId} />
      <div className="mt-6">
        <ProjectSidebarLayout
          tabItems={finalTabs}
          value={defaultTab}
          onValueChange={handleTabChange}
          isReadOnly={isReadOnly}
        />
      </div>
    </>
  );
};
